import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Icon } from '@fluentui/react';
import { useIsOverlayModalOpen } from 'shared-state/display-options/hooks';

interface IDraggableJitsiForPersonalDeskProps {
    children: React.ReactNode;
    initialPosition?: { right: number; bottom: number };
    style?: React.CSSProperties;
    jitsiSize?: { width: number; height: number };
    // Set for the layouts where the call isn't a floating tile (the meeting table, and the desk on
    // small screens): the container takes exactly this style and the drag handle goes. The same
    // component renders in every personal-room layout so that switching between them only restyles
    // the call - see RenderJitsiOrWidgets for why it must never remount.
    dockedStyle?: React.CSSProperties;
}

export const DraggableJitsiForPersonalDesk: React.FC<IDraggableJitsiForPersonalDeskProps> = ({
    children,
    initialPosition = { right: 5, bottom: 5 },
    style = {},
    jitsiSize = { width: 320, height: 280 },
    dockedStyle
}) => {
    const [position, setPosition] = useState(initialPosition);
    // This tile is a live-camera iframe that can end up on a hardware compositing layer painting
    // above everything else in the page regardless of z-index (see JitsiWrapper.tsx /
    // RoomCallController.state.tsx for the same issue on the non-personal-desk call view). Unlike
    // that view, this component owns its position entirely itself with no external "hidden" prop,
    // so when a widget overlay modal is open, override the applied position to push the whole tile
    // off-screen (an actual position change, not stacking) rather than touching `position` itself -
    // that state is the user's own drag placement and must survive the modal closing again.
    const isOverlayModalOpen = useIsOverlayModalOpen();
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const animationFrameId = useRef<number | null>(null);

    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const container = containerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
        setIsDragging(true);
    }, []);

    const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
        e.preventDefault(); // Prevent default touch behavior
        const container = containerRef.current;
        if (!container) return;

        const touch = e.touches[0];
        const rect = container.getBoundingClientRect();
        setDragOffset({
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top
        });
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !containerRef.current) return;

            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }

            animationFrameId.current = requestAnimationFrame(() => {
                const container = containerRef.current;
                const parent = container?.parentElement;
                if (!container || !parent) return;

                const parentRect = parent.getBoundingClientRect();
                const newX = e.clientX - parentRect.left - dragOffset.x;
                const newY = e.clientY - parentRect.top - dragOffset.y;

                const parentWidth = parentRect.width;
                const parentHeight = parentRect.height;
                const { width: jitsiWidth, height: jitsiHeight } = jitsiSize;

                const rightPercent = ((parentWidth - newX - jitsiWidth) / parentWidth) * 100;
                const bottomPercent = ((parentHeight - newY - jitsiHeight) / parentHeight) * 100;

                const minMargin = 3;
                const maxRight = 100 - minMargin - (jitsiWidth / parentWidth) * 100;
                const maxBottom = 100 - minMargin - (jitsiHeight / parentHeight) * 100;

                const clampedRight = Math.max(minMargin, Math.min(maxRight, rightPercent));
                const clampedBottom = Math.max(minMargin, Math.min(maxBottom, bottomPercent));

                setPosition({ right: clampedRight, bottom: clampedBottom });
            });
        },
        [isDragging, dragOffset, jitsiSize]
    );

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (!isDragging || !containerRef.current) return;

            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }

            animationFrameId.current = requestAnimationFrame(() => {
                const container = containerRef.current;
                const parent = container?.parentElement;
                if (!container || !parent) return;

                const touch = e.touches[0];
                const parentRect = parent.getBoundingClientRect();
                const newX = touch.clientX - parentRect.left - dragOffset.x;
                const newY = touch.clientY - parentRect.top - dragOffset.y;

                const parentWidth = parentRect.width;
                const parentHeight = parentRect.height;
                const { width: jitsiWidth, height: jitsiHeight } = jitsiSize;

                const rightPercent = ((parentWidth - newX - jitsiWidth) / parentWidth) * 100;
                const bottomPercent = ((parentHeight - newY - jitsiHeight) / parentHeight) * 100;

                const minMargin = 3;
                const maxRight = 100 - minMargin - (jitsiWidth / parentWidth) * 100;
                const maxBottom = 100 - minMargin - (jitsiHeight / parentHeight) * 100;

                const clampedRight = Math.max(minMargin, Math.min(maxRight, rightPercent));
                const clampedBottom = Math.max(minMargin, Math.min(maxBottom, bottomPercent));

                setPosition({ right: clampedRight, bottom: clampedBottom });
            });
        },
        [isDragging, dragOffset, jitsiSize]
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleTouchEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => handleMouseMove(e);
        const onMouseUp = () => handleMouseUp();
        const onTouchMove = (e: TouchEvent) => {
            e.preventDefault(); // Prevent scrolling while dragging
            handleTouchMove(e);
        };
        const onTouchEnd = () => handleTouchEnd();

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', onTouchEnd);

        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };
    }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    return (
        <div
            ref={containerRef}
            style={
                dockedStyle ?? {
                    position: 'absolute',
                    right: isOverlayModalOpen ? '150%' : `${position.right}%`,
                    bottom: isOverlayModalOpen ? '150%' : `${position.bottom}%`,
                    userSelect: 'none',
                    zIndex: 10,
                    width: `${jitsiSize.width}px`,
                    height: `${jitsiSize.height}px`,
                    ...style
                }
            }
        >
            {/* Conditional rather than hidden, which is safe: React keeps an empty slot here, so the
                call below stays at the same position among this div's children either way. */}
            {!dockedStyle && (
                <div
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    role="button"
                    aria-label="Drag to move"
                    style={{
                        position: 'absolute',
                        top: '-30px',
                        left: '5%',
                        transform: 'translateX(-50%)',
                        width: '24px',
                        height: '24px',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        zIndex: 11,
                        transition: 'background-color 0.2s ease',
                        touchAction: 'none' // Prevent default touch actions
                    }}
                >
                    <Icon
                        iconName="Move"
                        style={{
                            color: '#3B82F6',
                            fontSize: '24px',
                            fontWeight: 'bold'
                        }}
                    />
                </div>
            )}

            {children}
        </div>
    );
};

export default DraggableJitsiForPersonalDesk;
